import React, { ReactNode, useState, useEffect } from 'react';

import Button from 'common/components/Button';
import { ErrorMessages } from 'common/components/Form/ErrorMessages';
import withFormState, {
  WithFormStateProps
} from 'common/components/Form/withFormState';
import { NEWSLETTER_SUBSCRIPTION_PUSH } from 'common/constants/LocalStorageKeys';
import { isRequired, matchRegex, maxLength } from 'common/forms/validators';
import { path } from 'common/tools/network/routing';
import trans from 'common/tools/translations/trans';
import { isString } from 'common/types';

import { subscribeToNewsletterWithActito } from 'website/api/Actito';

import styles from './styles.module.scss';

const errorInvalidEmail = trans('error.invalid-email');

type Props = {
  campaignId: number;
  children?: ReactNode;
  infoMsg: string;
  onSubmitSuccess: () => void;
  title?: string;
};

type NewsletterFields = 'email' | 'honeypot';

const NewsletterForm = ({
  campaignId,
  children = null,
  formErrors,
  formValues,
  infoMsg = 'Subscribe to our newsletter',
  onFieldChange,
  onSubmitSuccess,
  title,
  validateForm
}: WithFormStateProps<NewsletterFields> & Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [code, setCode] = useState();
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(
    isString(formValues.email) ? formValues.email : undefined
  );
  const [honeypot, setHoneypot] = useState(
    isString(formValues.honeypot) ? formValues.honeypot : undefined
  );

  useEffect(() => onFieldChange('email', email), [email]);
  useEffect(() => onFieldChange('honeypot', honeypot), [honeypot]);

  useEffect(() => {
    if (submitSuccess && submitted) {
      localStorage.setItem(NEWSLETTER_SUBSCRIPTION_PUSH, 'true');
      onSubmitSuccess();
    }
  }, [submitSuccess, submitted, onSubmitSuccess]);

  const messageClass = code === 409 ? 'warning' : 'danger';

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const isValid = await validateForm();
    if (!isValid) {
      setSubmitting(false);
      return;
    }

    try {
      const result = await subscribeToNewsletterWithActito(
        formValues.email,
        campaignId
      );
      setSubmitSuccess(!result.error);
      setMessage(result.message);
      setCode(result.code);
    } catch (err) {
      setSubmitSuccess(false);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <form className={styles.module} onSubmit={handleEmailSubmit}>
      <div className={styles.moduleContainer}>
        {title ? <div className={styles.moduleTitle}>{title}</div> : null}
        <div className={styles.moduleInfo}>{infoMsg}</div>
        <div className={styles.emailInputContainer}>
          <input
            type="email"
            disabled={submitting}
            className={styles.emailInput}
            placeholder={trans('newsletter-modal.email')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,4}$"
            required
          />
          <input
            name="_gotcha"
            onChange={e => setHoneypot(e.target.value)}
            style={{ display: 'none' }}
            type="text"
            value={honeypot}
          />
          {formErrors.email && <ErrorMessages messages={formErrors.email} />}
        </div>
        <div className={styles.emailSubmitContainer}>
          <Button disabled={submitting} submit theme="inverse">
            {trans('newsletter-modal.submit')}
          </Button>
        </div>
        <div className={styles.emailLegalLink}>
          <a
            href={`${path('privacy_policy')}`}
            rel="noopener noreferrer"
            target="_blank"
            className="light"
          >
            {trans('newsletter-modal.legal-link')}
          </a>
        </div>
        {submitted && !submitSuccess && (
          <ErrorMessages messages={[message]} className={messageClass} />
        )}
        {children}
      </div>
    </form>
  );
};

export default withFormState<Props, NewsletterFields>({
  fields: ['email', 'honeypot'],
  validators: {
    email: [
      isRequired(trans('error.isrequired')),
      matchRegex(/[a-z\d._%+-]+@[a-z\d.-]+.[a-z]{2,4}$/, errorInvalidEmail)
    ],
    honeypot: [maxLength(0, errorInvalidEmail)]
  },
  defaultValues: { email: '', honeypot: '' }
})(NewsletterForm);
